import React, { useState, useEffect } from "react";
import { useOSDetail, isStaleVersionError } from "../hooks/useOSDetail";
import { useOSKeepAliveRefresh } from "../hooks/useOSKeepAliveRefresh";
import { useOSArchive } from "../hooks/useOSArchive";
import { useNavigate } from "react-router-dom";
import Navbar from "../components/Navbar";
import OSMasterHeader from "../components/OSMasterHeader";
import { OSNormativStatus } from "../components/OSNormativStatusBadge";
import OSDetailHeader from "../components/OSDetailHeader";
import OSArchiveModal, { OSArchiveOptions } from "../components/OSArchiveModal";
import OSArchiveInfoBox from "../components/OSArchiveInfoBox";
import { useSubprocesses } from "../select/SubprocessCells";
import ModalConfirmation from "../modals/ModalConfirmation";
import ModalSignPin from "../modals/ModalSignPin";
import UnsavedChangesModal from "../components/UnsavedChangesModal";
import { Ingredient, Item, FormulationBatchRow } from "../types/Types";
import { useTranslation } from "react-i18next";
import LoadingSpinner from "../assets/LoadingSpinner";
import ErrorComponent from "../assets/ErrorComponent";
import { ItemCacheProvider } from '../context/ItemCacheContext';
import { useItemCache } from '../context/ItemCacheContext';
import ShortcutNavBar from "../components/ShortcutNavBar";
import { useOSExports } from "../hooks/useOSExports";
import { useToast } from "../context/ToastContext";
import { useShortcut } from "../context/ShortcutContext";
import LogBookToggleButton from "../components/LogBookToggleButton";
import OSStageTree, { RAZMER_STAGE_ID } from "../components/OSStageTree";
import OSStagePanel from "../components/OSStagePanel";
import useStageAutosave from "../hooks/useStageAutosave";
import AutosaveStatusPill from "../components/AutosaveStatusPill";
import OSSubprocessMachinesTable from "../components/OSSubprocessMachinesTable";
import { RazmerRow, emptyRazmerRow, allowedRangeOf, allowedRangeNumberOf, razmerRowHasData, razmerRowMissingSubprocess } from "../components/OSRazmerTable";
import { mapRazmerRows, sortRazmerByPhase, mapInterProductRows, mapMaterialnoGroups, mapProductivityRows, mapParamValueMap } from "../utils/osIngredientMappers";
import { parseDecimalInput } from "../utils/decimalInput";
import { buildSubprocessOrderIndex } from "../utils/subprocessOrder";
import { MatGroup, MatItem, emptyGroup } from "../components/OSMaterialnoTable";
import OSProductivityTable, { ProdRow, emptyProdRow } from "../components/OSProductivityTable";
import { isPerSubprocessStage } from "../utils/logbookStage";
import { SubprocessItemRow, emptySubprocessItemRow, allowedYieldOf, deriveIssueSubprocessIds } from "../components/OSSubprocessItemsTable";
import { useMachineOptions } from "../select/MachineSelect";
import { useProcessDefinitions } from "../select/ProcessCells";

const formatTodayDDMMYYYY = () => new Date().toLocaleDateString('en-GB').replace(/\//g, '.');

const isValidToExtension = (nextValue: string, currentValue: string): boolean => {
  if (!currentValue) return false;
  if (!nextValue) return true;
  const toComparableDate = (value: string) => {
    const match = /^(\d{2})\.(\d{2})\.(\d{4})$/.exec(value);
    return match ? Number(`${match[3]}${match[2]}${match[1]}`) : null;
  };
  const next = toComparableDate(nextValue);
  const current = toComparableDate(currentValue);
  return next !== null && current !== null && next > current;
};

/**
 * `Ingredient.consumedInGroups` as this screen carries it.
 *
 * There is no editor for it on /os any more — the „Материјално / Процес" tabs card that owned
 * it was removed, together with the whole OSMaterialTable/useOSMaterialSpreadsheet cluster.
 * The field is still read on load and written on save as a lossless passthrough so no persisted
 * data is lost, which is the only reason this shape still needs a name here. No screen edits it
 * any more — /rn-work-order's twin of that editor was deleted too, and RN never wrote the field.
 */
export interface ConsumedInGroupValue {
  item: string;
  quantity: number | null;
  unitMeasurement: string | null;
  priority: number | null;
  process: string | null;
  subprocess: string | null;
  waste: number | null;
}

interface OSInnerProps {
  archiveView?: boolean;
}

const OSInner: React.FC<OSInnerProps> = ({ archiveView = false }) => {
  const { t } = useTranslation();
  const { showToast } = useToast();
  const navigate = useNavigate();
  const { addShortcut, hasShortcut } = useShortcut();
  const isArchiveView = archiveView;
  const { getItem, addItemsToCache, fetchItemById } = useItemCache();
  // Module-cached — safe to call here; SubprocessCells already calls it, no duplicate fetch.
  // Used for the stage tree's active subprocess and the entry-owner map. The codebooks that
  // only ever fed the Excel exports (phase types / machines / document types) now live in
  // useOSExports instead.
  const subprocesses = useSubprocesses();
  const machineOptions = useMachineOptions();
  const processDefinitions = useProcessDefinitions();

  // Left vertical stage tree — groups/stages driven by process-definitions / subprocesses codebooks
  const [activeStage, setActiveStage] = useState<string>('');
  // Per-ident selected subprocesses (checkboxes in master header → filter left stage tree)
  // Per-ident Продуктивност rows (persisted). Starts with 1 row; ArrowDown adds more.
  const [productivityRows, setProductivityRows] = useState<ProdRow[]>(() => [emptyProdRow()]);
  // Per-ident Размерување rows (persisted)
  const [razmerRows, setRazmerRows] = useState<RazmerRow[]>(() => [emptyRazmerRow()]);
  // Previous render's set of `subprocessId|itemId` SEED keys — from BOTH sources that feed
  // Материјално (Размерување + Меѓу/Полупроизвод). Input to the un-seeding effect further down.
  // Reset to null whenever a different record is loaded so the outgoing record's keys can never
  // be diffed against the incoming one's.
  const prevSeedKeysRef = React.useRef<Set<string> | null>(null);
  // Per-ident Материјално groups (persisted)
  const [materialnoGroups, setMaterialnoGroups] = useState<MatGroup[]>(() => [emptyGroup(0, "1")]);
  // Per-ident Меѓу/Полупроизвод rows (refs + quantity; ident/name/ЕМ derived from item)
  const [subprocessItemRows, setSubprocessItemRows] = useState<SubprocessItemRow[]>(() => [emptySubprocessItemRow()]);
  // InProcess values keyed by Subprocess.inProcess entry _id; persisted per ident.
  const [inProcessValues, setInProcessValues] = useState<Record<string, string>>({});
  // Параметри за монтажа values keyed by Subprocess.montageParams entry _id; persisted per ident.
  const [montageValues, setMontageValues] = useState<Record<string, string>>({});
  // entry _id -> owning subprocess _id, across both codebook arrays, so a saved value
  // knows which subprocess it belongs to
  const entryOwner = React.useMemo(() => {
    const map: Record<string, string> = {};
    subprocesses.forEach((sp) => {
      (sp.inProcess || []).forEach((e) => { map[e._id] = sp._id; });
      (sp.montageParams || []).forEach((e) => { map[e._id] = sp._id; });
    });
    return map;
  }, [subprocesses]);

  // Which stages this ident has — and what gets saved to `Ingredient.subprocesses`.
  //
  // PURELY DERIVED: a subprocess belongs to the normativ if and only if something on the
  // normativ actually carries data for it — a row in Меѓу/Полупроизвод (either the receiving
  // or the issuing subprocess of an inter-product row), Размерување, Материјално or
  // Продуктивност, or a filled-in InProcess / Параметри за монтажа value.
  // This is the design the codebase already documents: see OSSubprocessCheckboxes.tsx, where
  // the per-ident picker was deleted precisely because selection "is derived, not picked".
  //
  // The previously loaded `Ingredient.subprocesses` is deliberately NOT unioned in any more.
  // It used to be, which made a stage impossible to remove: nothing in the UI could edit that
  // list, so once saved a subprocess was re-added to the union on every subsequent save even
  // after the user had deleted every row referencing it. Dropping it restores the symmetry —
  // delete the last thing referencing a stage and the stage goes away.
  //
  // The two param maps MUST be part of this: handleSaveIngredient persists only the entries
  // whose owning subprocess is in this list, so a stage carrying nothing but InProcess values
  // would otherwise drop out and silently take those values with it.
  const selectedSubprocessIds = React.useMemo(() => {
    const ids = new Set<string>();
    subprocessItemRows.forEach((r) => {
      if (r.subprocessId) ids.add(r.subprocessId);
      if (r.issueSubprocessId) ids.add(r.issueSubprocessId);
    });
    razmerRows.forEach((r) => { if (r.subprocessId) ids.add(r.subprocessId); });
    // A blank placeholder Постапка (no items, no text) does not hold its stage.
    materialnoGroups.forEach((g) => {
      const blank = g.items.length === 0 && !(g.procedure || "").trim() && !(g.instruction || "").trim();
      if (g.subprocessId && !blank) ids.add(g.subprocessId);
    });
    // A Log Book row holds its stage only when it carries a time or a machine — a per-subprocess
    // row with nothing but an operator count (Log Book has no delete) must not pin the stage.
    productivityRows.forEach((r) => {
      const hasTime = r.machineId || r.montageTime || r.machineTime || r.cleaningTime || r.dismountCleanTime;
      if (r.subprocessId && hasTime) ids.add(r.subprocessId);
    });
    const addEntryOwners = (values: Record<string, string>) => {
      Object.entries(values).forEach(([entryId, value]) => {
        if (value.trim() === "") return;
        const owner = entryOwner[entryId];
        if (owner) ids.add(owner);
      });
    };
    addEntryOwners(inProcessValues);
    addEntryOwners(montageValues);
    return Array.from(ids).filter((id) => id !== RAZMER_STAGE_ID);
  }, [subprocessItemRows, razmerRows, materialnoGroups, productivityRows, inProcessValues, montageValues, entryOwner]);
  // Archive-view data (/os/archive) — fetching lives in the hook; inert on the live screen.
  const {
    archiveIngredients,
    loadingArchive,
    setSelectedArchiveId,
    archiveDetail,
    loadingArchiveDetail,
  } = useOSArchive({ archiveView: isArchiveView });

  // Dirty tracking for unsaved changes
  const [isDirty, setIsDirty] = useState<boolean>(false);
  const [hasGmpChanges, setHasGmpChanges] = useState(false);
  const markGmpDirty = React.useCallback(() => {
    setHasGmpChanges(true);
    setIsDirty(true);
  }, []);


  // „Освежи" on a version-conflict toast — wired below, once applyIngredientSelection exists.
  const staleReloadRef = React.useRef<() => void>(() => {});
  const handleStaleReload = React.useCallback(() => staleReloadRef.current(), []);

  const {
    ingredient,
    clearIngredient,
    loadingIngredient,
    ingredients,
    loadingList,
    errorList,
    page,
    totalPages,
    absoluteTotal,
    setPage,
    search,
    setSearch,
    createIngredient,
    loadIngredientById,
    refreshIngredientIfChanged,
    fetchIngredientByItem,
    updateIngredient,
    deleteIngredient,
    archiveIngredient,
    reloadIngredientsList,
    usersList,
    signIngredient,
    removeIngredientSignature,
  } = useOSDetail({ archiveView: isArchiveView, onStaleVersion: handleStaleReload });

  // In archive view the master-header display fields must reflect the selected archived
  // record, not the last live-loaded `ingredient` (which stays cached in the hook).
  const displayIngredient: any = isArchiveView ? archiveDetail : ingredient;

  const [confirmDeleteModal, setConfirmDeleteModal] = useState(false);
  const [confirmPreparedSave, setConfirmPreparedSave] = useState(false);
  const [showArchiveModal, setShowArchiveModal] = useState(false);
  // Normativ selected in the master-header search while the form was dirty — held here until
  // the user answers the guard modal. Mirrors HRBasicData's pendingEmployeeRef pattern.
  const pendingIngredientRef = React.useRef<Ingredient | null>(null);
  const [showUnsavedModal, setShowUnsavedModal] = useState(false);

  const handleArchive = () => {
    if (!ingredient?._id) return;
    setShowArchiveModal(true);
  };

  const handleOpenCopyIngredients = () => {
    if (!hasShortcut('/os')) {
      addShortcut({
        documentCode: t('sidebar.osBasicSpecification') || 'OS',
        documentName: t('sidebar.osBasicSpecification') || 'OS',
        category: 'ingredients',
        path: '/os',
      });
    }
    if (!hasShortcut('/copy-ingredients')) {
      addShortcut({
        documentCode: t('sidebar.copyIngredients') || 'Copy Ingredients',
        documentName: t('sidebar.copyIngredients') || 'Copy Ingredients',
        category: 'ingredients',
        subCategory: 'copy-ingredients',
        path: '/copy-ingredients',
      });
    }

    navigate('/copy-ingredients', {
      state: {
        sourceIngredientId: !isArchiveView && displayIngredient?._id ? displayIngredient._id : null,
      },
    });
  };

  const archiveValidTo = showArchiveModal ? formatTodayDDMMYYYY() : '';

  const handleArchiveConfirm = async (reason: string, archiveDateTo?: string, options?: OSArchiveOptions) => {
    if (!ingredient?._id) return;
    const result = await archiveIngredient(
      ingredient._id,
      reason,
      archiveDateTo || formatTodayDDMMYYYY(),
      !!options?.createNewVersion
    );
    setShowArchiveModal(false);
    const newVersion = options?.createNewVersion ? result?.ingredient : null;
    if (newVersion?._id) {
      setLastSyncedIngredientId(null);
      await loadIngredientById(newVersion._id);
      setIsCreating(false);
    }
  };

  const [isCreating, setIsCreating] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [showTotalProductivity, setShowTotalProductivity] = useState(false);
  const [showValidationErrors, setShowValidationErrors] = useState(false);
  const [selectedItemCode, setSelectedItemCode] = useState<string>("");

  // Form fields
  const [selectedItemId, setSelectedItemId] = useState<string>("");
  const [batch, setBatch] = useState<number>(0);
  const [batchUnit, setBatchUnit] = useState<string | null>(null);
  const [batchUnitCode, setBatchUnitCode] = useState<string>("");
  const [waste, setWaste] = useState<number>(0);
  const [formulationBatchRows, setFormulationBatchRows] = useState<FormulationBatchRow[]>([]);
  const [consumedInGroups, setConsumedInGroups] = useState<ConsumedInGroupValue[]>([]);
  const [plannedWaste, setPlannedWaste] = useState<number>(0);
  const [validFrom, setValidFrom] = useState<string>("");
  const [validTo, setValidTo] = useState<string>("");
  const [selectedWorkOrderDocumentId, setSelectedWorkOrderDocumentId] = useState<string>("");
  const [status, setStatus] = useState<string[]>(['active']);
  const [versionDisplay, setVersionDisplay] = useState<string>("001");
  const [productionCode, setProductionCode] = useState<string>("");

  // Signature panel state (prepared / approved — mirrors QA flow)
  const [preparedUserId, setPreparedUserId] = useState<string>("");
  const [preparedDate, setPreparedDate] = useState<string>("");
  const [approvedUserId, setApprovedUserId] = useState<string>("");
  const [approvedDate, setApprovedDate] = useState<string>("");
  const [signField, setSignField] = useState<"prepared" | "approved" | null>(null);
  const [removePreparedSignature, setRemovePreparedSignature] = useState(false);

  // Material tab process parameters (Master Batch Record fields)
  const [equipmentId, setEquipmentId] = useState<string>("");

  // Track the last synced ingredient ID to prevent duplicate syncs
  const [lastSyncedIngredientId, setLastSyncedIngredientId] = useState<string | null>(null);
  // Mirrors which ingredient syncIngredientFields is currently loading, via ref (not state) so
  // the async work-order-type backfill below can check it synchronously inside its .then() -
  // guards against the user opening a different record before fetchItemById resolves.
  const currentSyncIdRef = React.useRef<string | null>(null);

  // "Тип на РН" is derived from the item's classification (tercialClass.workOrderType), not
  // chosen by the user - mirrors how allowedFinishedWasteOf is derived in OSDetailHeader.tsx.
  // Shared by handleSelectedItemChange (user picks an item - always derive) and
  // syncIngredientFields (opening a record whose saved workOrderDocument is empty - backfill
  // only, never overwrite an existing saved value).
  const applyWorkOrderType = React.useCallback((item: Item | null) => {
    if (!item) return;
    const tercialClass = (item as any).tercialClass;
    const workOrderType = tercialClass && typeof tercialClass === 'object' ? tercialClass.workOrderType : null;
    const workOrderTypeId = workOrderType && typeof workOrderType === 'object' ? workOrderType._id || '' : workOrderType || '';
    setSelectedWorkOrderDocumentId(workOrderTypeId ? String(workOrderTypeId) : '');
  }, []);

  // Sync ingredient to form fields
  const syncIngredientFields = React.useCallback((ing: Ingredient) => {
    currentSyncIdRef.current = ing._id;
    // Preload item into cache FIRST to ensure names are available immediately
    if (typeof ing.items === 'object' && ing.items && ing.items._id && ing.items.ident && ing.items.name) {
      // Cast to Item type since we've verified required fields exist
      addItemsToCache([ing.items as Item]);
    }

    const itemId = typeof ing.items === 'string' ? ing.items : ing.items?._id || '';
    setSelectedItemId(itemId);

    // Set item code for display
    if (typeof ing.items === 'object' && ing.items) {
      setSelectedItemCode(ing.items.ident || ing.items.name || '');
    } else if (itemId && getItem) {
      const cachedItem = getItem(itemId);
      if (cachedItem) {
        setSelectedItemCode(cachedItem.ident || cachedItem.name || '');
      }
    }

    setBatch(ing.batch || 0);
    const itemMainUnit = typeof ing.items === 'object' && ing.items && (ing.items as any).mainUnit
      ? (typeof (ing.items as any).mainUnit === 'object' ? (ing.items as any).mainUnit?._id || null : (ing.items as any).mainUnit)
      : null;
    setBatchUnit(itemMainUnit);
    const itemMainUnitCode = typeof ing.items === 'object' && ing.items
      ? ((typeof (ing.items as any).mainUnit === 'object' ? (ing.items as any).mainUnit?.code : undefined) || (ing.items as any).unitMeasurment?.code || '')
      : '';
    setBatchUnitCode(itemMainUnitCode);
    setWaste(ing.waste || 0);
    if (Array.isArray(ing.formulationBatch) && ing.formulationBatch.length > 0) {
      setFormulationBatchRows(
        (ing.formulationBatch as any[]).map((r) => ({
          _id: r._id,
          text: r.text || '',
          item: typeof r.item === 'object' && r.item ? r.item._id : (r.item || ''),
          ident: typeof r.item === 'object' && r.item ? r.item.ident || '' : '',
          name: typeof r.item === 'object' && r.item ? r.item.name || '' : '',
          process: typeof r.process === 'object' && r.process ? r.process._id : (r.process || ''),
          processDefinition: typeof r.processDefinition === 'object' && r.processDefinition ? (r.processDefinition as any)._id : (r.processDefinition || ''),
          machine: typeof r.machine === 'object' && r.machine ? r.machine._id : (typeof r.machine === 'string' ? r.machine : ''),
          machineCode: typeof r.machine === 'object' && r.machine ? r.machine.code || '' : '',
          machineName: typeof r.machine === 'object' && r.machine ? r.machine.name || '' : '',
          machineSetupTime: typeof r.machine === 'object' && r.machine ? r.machine.setupTime ?? null : null,
          machineRunTimeMin: r.machineRunTimeMin ?? null,
          cleaningTimeMin: r.cleaningTimeMin ?? null,
          machineInstructions: r.machineInstructions || '',
          procedure: r.procedure ?? '',
          operatorCount: r.operatorCount ?? null,
          rowWaste: r.waste ?? null,
          priority: r.priority ?? null,
          quantity: r.quantity || 0,
          mainUnit: typeof r.mainUnit === 'object' ? r.mainUnit?._id || '' : r.mainUnit || '',
          params: r.params || null,
          qualDoc: typeof r.qualDoc === 'object' && r.qualDoc ? (r.qualDoc as any)._id || '' : (r.qualDoc as string || ''),
          qualDocCode: typeof r.qualDoc === 'object' && r.qualDoc ? (r.qualDoc as any).code || '' : '',
        }))
      );
    } else {
      // Pre-seed with one empty row so FormulationBatchTable's auto-seed effect
      // doesn't fire onChange → setIsDirty(true) on load.
      setFormulationBatchRows([{ text: '', item: '', ident: '', name: '', process: '', processDefinition: '', machine: '', machineCode: '', machineName: '', machineSetupTime: null, machineRunTimeMin: null, cleaningTimeMin: null, machineInstructions: '', procedure: '', operatorCount: null, rowWaste: null, priority: null, quantity: 0, mainUnit: '', params: null, qualDoc: '', qualDocCode: '' }]);
    }
    setConsumedInGroups(
      Array.isArray(ing.consumedInGroups)
        ? ing.consumedInGroups.map((group: any) => {
            const refId = (raw: any) => (typeof raw === 'object' ? raw?._id || null : raw || null);
            return {
              item: typeof group.item === 'object' ? group.item?._id || '' : group.item || '',
              quantity: group.quantity ?? null,
              unitMeasurement: refId(group.unitMeasurement),
              priority: group.priority ?? null,
              process: refId(group.process),
              subprocess: refId(group.subprocess),
              waste: group.waste ?? null,
            };
          }).filter((group) => !!group.item)
        : []
    );
    setPlannedWaste(ing.plannedWaste || 0);
    setValidFrom(ing.validFrom || '');
    setValidTo(ing.validTo || '');
    const savedWorkOrderDocumentId = typeof ing.workOrderDocument === 'string' ? ing.workOrderDocument : ing.workOrderDocument?._id || '';
    setSelectedWorkOrderDocumentId(savedWorkOrderDocumentId);
    // Backfill only: records saved before "Тип на РН" was derived from classification have no
    // workOrderDocument at all. Never overwrite a saved value - a later classification change
    // must not silently rewrite old ОС records.
    if (!savedWorkOrderDocumentId && itemId) {
      const cachedItem = getItem(itemId);
      if (cachedItem) {
        applyWorkOrderType(cachedItem);
      } else {
        const syncedIngId = ing._id;
        fetchItemById(itemId)
          .then((item) => {
            // Race guard: bail if a different record was opened while this fetch was in flight.
            if (currentSyncIdRef.current === syncedIngId) {
              applyWorkOrderType(item);
            }
          })
          .catch(() => {});
      }
    }
    // Handle migration from old boolean/string format to new string array format
    if (Array.isArray(ing.status)) {
      setStatus(ing.status);
    } else if (typeof ing.status === 'boolean') {
      setStatus(ing.status ? ['active'] : ['inactive']);
    } else {
      setStatus(ing.status === 'active' ? ['active'] : ['inactive']);
    }

    // Signature panel sync
    const prepUser = (ing as any).prepared?.user;
    const appUser = (ing as any).approved?.user;
    setPreparedUserId(typeof prepUser === 'object' && prepUser ? prepUser._id : (prepUser || ''));
    setApprovedUserId(typeof appUser === 'object' && appUser ? appUser._id : (appUser || ''));
    const prepDateRaw = (ing as any).prepared?.date;
    const appDateRaw = (ing as any).approved?.date;
    setPreparedDate(prepDateRaw ? new Date(prepDateRaw).toLocaleDateString('en-GB').replace(/\//g, '.') : '');
    setApprovedDate(appDateRaw ? new Date(appDateRaw).toLocaleDateString('en-GB').replace(/\//g, '.') : '');

    setEquipmentId(ing.equipmentId || '');
    // `ing.subprocesses` is intentionally NOT read into state: the stage list is derived from
    // the four per-stage arrays below (see selectedSubprocessIds). Mirroring the stored list
    // was what made a subprocess impossible to remove.
    setProductivityRows(mapProductivityRows((ing as any).productivity));
    const mappedRazmer = sortRazmerByPhase(mapRazmerRows((ing as any).razmer));
    setRazmerRows(mappedRazmer);
    // A freshly loaded record starts a new diff baseline — see the un-seeding effect.
    prevSeedKeysRef.current = null;
    const mappedInterProducts = mapInterProductRows((ing as any).interProducts);
    if (mappedInterProducts.length === 0) mappedInterProducts.push(emptySubprocessItemRow());
    setSubprocessItemRows(mappedInterProducts);
    setMaterialnoGroups(mapMaterialnoGroups((ing as any).materialno, mappedRazmer));
    setInProcessValues(mapParamValueMap((ing as any).inProcessParams));
    setMontageValues(mapParamValueMap((ing as any).montageParamValues));
    const rawVersion = ing.versionDisplay || String(ing.version || 1).padStart(3, '0');
    const normalizedVersion = rawVersion.startsWith('v') ? rawVersion : `v${rawVersion}`;
    setVersionDisplay(ing.approved?.signature ? normalizedVersion : `${normalizedVersion} · нацрт`);
    setProductionCode((ing as any).productionCode || "");
    setIsCreating(false);
    // Rebind the left tree to the pinned weighing stage. `activeStage` is otherwise only ever
    // set by OSStageTree, so it survived a switch to another ident — and since the tree is
    // filtered by THIS normativ's subprocesses, the previous ident's stage could stay active
    // while being invisible in the tree: the tables below kept editing it, and typing there
    // grafted that subprocess onto the newly loaded normativ via selectedSubprocessIds.
    // This runs only when a DIFFERENT record is synced, so it never steals the user's stage
    // selection mid-edit.
    setActiveStage(RAZMER_STAGE_ID);
    setLastSyncedIngredientId(ing._id);
    setIsDirty(false);
    setHasGmpChanges(false);
  }, [getItem, addItemsToCache, fetchItemById, applyWorkOrderType]);

  const handleSelectedItemChange = React.useCallback((itemId: string) => {
    setSelectedItemId(itemId);
    markGmpDirty();

    if (!itemId) {
      setBatchUnit(null);
      setBatchUnitCode("");
      setSelectedWorkOrderDocumentId("");
      return;
    }

    const applyMainUnit = (item: Item | null) => {
      if (!item) return;
      const mainUnit = item.mainUnit;
      const mainUnitId = mainUnit && typeof mainUnit === 'object' ? mainUnit._id || null : mainUnit || null;
      setBatchUnit(mainUnitId);
      const mainUnitCode = (mainUnit && typeof mainUnit === 'object' ? mainUnit.code : undefined) || (item as any).unitMeasurment?.code || '';
      setBatchUnitCode(mainUnitCode);
    };

    // "Тип на РН" is derived from the item's classification - see the shared applyWorkOrderType
    // defined above (also used by syncIngredientFields to backfill older records).
    const cachedItem = getItem(itemId);
    if (cachedItem) {
      applyMainUnit(cachedItem);
      applyWorkOrderType(cachedItem);
      return;
    }

    fetchItemById(itemId)
      .then((item) => {
        applyMainUnit(item);
        applyWorkOrderType(item);
      })
      .catch(() => {});
  }, [fetchItemById, getItem, applyWorkOrderType]);

  // Sync loaded ingredient to local form state.
  // Skip in archive view — there the source of truth is `archiveDetail` (synced by the
  // effect below). Running both fights over `lastSyncedIngredientId` → infinite re-render.
  useEffect(() => {
    if (!isArchiveView && ingredient && ingredient._id !== lastSyncedIngredientId) {
      syncIngredientFields(ingredient);
    }
  }, [isArchiveView, ingredient, lastSyncedIngredientId, syncIngredientFields]);

  // Update item code when selectedItemId changes
  useEffect(() => {
    if (selectedItemId && getItem) {
      const cachedItem = getItem(selectedItemId);
      if (cachedItem) {
        setSelectedItemCode(cachedItem.ident || cachedItem.name || '');
      }
    } else if (!selectedItemId) {
      setSelectedItemCode('');
    }
  }, [selectedItemId, getItem]);

  // Keep a ref to the latest loaded ingredient so the rebind effect below can read it
  // without adding `ingredient` to its own deps (that would re-run on every load/sync).
  const ingredientRef = React.useRef(ingredient);
  ingredientRef.current = ingredient;

  // Rebind the editor to the normativ matching the currently selected ident.
  // The screen auto-loads the latest normativ on mount, but if the user then picks a
  // DIFFERENT ident in the header, `ingredient._id` keeps pointing at the previously
  // loaded doc — Save would then PUT that old doc with the new `items`, colliding with
  // the newly selected ident's own normativ. Mirrors RNWorkOrder.tsx's loadRNByItem effect.
  useEffect(() => {
    if (isArchiveView) return;
    if (!selectedItemId) return;

    const loaded = ingredientRef.current;
    const loadedItemId = loaded
      ? String((loaded as any).items?._id ?? (loaded as any).items ?? "")
      : "";
    if (loadedItemId === selectedItemId) {
      // Already bound to the selected ident — nothing to do.
      return;
    }

    let cancelled = false;
    (async () => {
      const found = await fetchIngredientByItem(selectedItemId);
      if (cancelled) return;
      if (found) {
        if (found._id !== ingredientRef.current?._id) await loadIngredientById(found._id);
        return;
      }
      // No normativ exists for this ident yet. If a different ident's normativ is still
      // loaded, clear it and switch to create mode so Save creates a new normativ instead
      // of PUT-ing the wrong doc.
      const stillLoaded = ingredientRef.current;
      const stillLoadedItemId = stillLoaded
        ? String((stillLoaded as any).items?._id ?? (stillLoaded as any).items ?? "")
        : "";
      if (stillLoaded && stillLoadedItemId !== selectedItemId) {
        clearIngredient();
        // Same stale-stage reset as syncIngredientFields/resetForm: the editor just
        // rebound to a different ident, so the previous ident's stage must not stay active.
        setActiveStage(RAZMER_STAGE_ID);
        setIsCreating(true);
      }
    })();
    return () => { cancelled = true; };
    // These three are useCallback-stable in useOSDetail — this effect must fire ONLY when the
    // selected ident changes. If any loses its stable identity, the effect runs on every
    // render and, for an ident with no normativ yet, fires a GET /by-item/:id per keystroke.
  }, [isArchiveView, selectedItemId, fetchIngredientByItem, loadIngredientById, clearIngredient]);

  // Archive view: load the selected archived record into the shared editor state so the
  // full normativ layout renders it. Read-only is enforced by the disabled <fieldset> wrapper.
  useEffect(() => {
    const archId = (archiveDetail as any)?._id;
    if (isArchiveView && archId && archId !== lastSyncedIngredientId) {
      syncIngredientFields(archiveDetail as any);
    }
  }, [isArchiveView, archiveDetail, lastSyncedIngredientId, syncIngredientFields]);

  const resetForm = () => {
    clearIngredient();
    setLastSyncedIngredientId(null);
    setSelectedItemId("");
    setSelectedItemCode("");
    setBatch(0);
    setBatchUnit(null);
    setBatchUnitCode("");
    setWaste(0);
    setFormulationBatchRows([{ text: '', item: '', ident: '', name: '', process: '', processDefinition: '', machine: '', machineCode: '', machineName: '', machineSetupTime: null, machineRunTimeMin: null, cleaningTimeMin: null, machineInstructions: '', procedure: '', operatorCount: null, rowWaste: null, priority: null, quantity: 0, mainUnit: '', params: null }]);
    setConsumedInGroups([]);
    setPlannedWaste(0);
    setValidFrom("");
    setValidTo("");
    setSelectedWorkOrderDocumentId("");
    setStatus(['active']);
    setPreparedUserId("");
    setPreparedDate("");
    setApprovedUserId("");
    setApprovedDate("");
    setEquipmentId("");
    setProductivityRows([emptyProdRow()]);
    setRazmerRows([emptyRazmerRow()]);
    prevSeedKeysRef.current = null;
    setMaterialnoGroups([emptyGroup(0, "1")]);
    setSubprocessItemRows([emptySubprocessItemRow()]);
    setInProcessValues({});
    setMontageValues({});
    setVersionDisplay("001");
    setProductionCode("");
    // Same reason as in syncIngredientFields: a stage left over from the previous ident has
    // no place in a blank form (its tree entry is gone, but the tables would still edit it).
    setActiveStage(RAZMER_STAGE_ID);
    setIsCreating(true);
    setIsDirty(false);
    setHasGmpChanges(false);
  };

  // Derive active-state from the persisted, loaded ingredient (NOT the local status form state)
  // so an unsaved status edit can't unlock the delete button - mirrors syncIngredientFields' status handling.
  const isLoadedIngredientActive = React.useMemo(() => {
    if (!ingredient) return false;
    const persistedStatus = (ingredient as any).status;
    if (Array.isArray(persistedStatus)) {
      return persistedStatus.includes('active');
    } else if (typeof persistedStatus === 'boolean') {
      return persistedStatus;
    } else {
      return persistedStatus === 'active';
    }
  }, [ingredient]);

  const handleDeleteRequest = async () => {
    if (!ingredient || !ingredient._id) { return; }
    setConfirmDeleteModal(true);
  };

  const handleDeleteCancel = () => {
    setConfirmDeleteModal(false);
  };

  const handleDeleteConfirm = async () => {
    if (!ingredient || !ingredient._id) {
      return;
    }
    try {
      await deleteIngredient(ingredient._id);
      // Don't call resetForm - let auto-load handle loading next ingredient
    } catch (err) {
      // Error toast already shown by deleteIngredient; swallow to avoid unhandled rejection
    } finally {
      setConfirmDeleteModal(false);
    }
  };

  // Load a normativ picked in the master-header search into the editor. Split out of
  // onIngredientSelect so the unsaved-changes guard can defer it and replay it on confirm.
  const applyIngredientSelection = React.useCallback((ing: Ingredient) => {
    setIsDirty(false);
    setHasGmpChanges(false);
    // Preload item into cache FIRST if available
    if (typeof ing.items === 'object' && ing.items && ing.items._id && ing.items.ident && ing.items.name) {
      addItemsToCache([ing.items as Item]);
    }
    // Reset synced ingredient ID to allow new ingredient sync
    setLastSyncedIngredientId(null);
    // Load the selected ingredient - this will trigger syncIngredientFields
    // which will set all fields properly
    loadIngredientById(ing._id);
    setIsCreating(false);
  }, [addItemsToCache, loadIngredientById]);

  // Version conflict → reload the normativ into the editor (the user chose to drop their edits).
  staleReloadRef.current = () => {
    if (ingredient?._id) applyIngredientSelection(ingredient);
  };
  useOSKeepAliveRefresh({
    ingredientId: ingredient?._id,
    isDirty,
    disabled: isArchiveView || isCreating,
    refreshIngredientIfChanged,
    onRefreshed: () => setLastSyncedIngredientId(null),
  });

  const handleUnsavedConfirm = React.useCallback(() => {
    setShowUnsavedModal(false);
    const pending = pendingIngredientRef.current;
    pendingIngredientRef.current = null;
    if (pending) {
      applyIngredientSelection(pending);
    }
  }, [applyIngredientSelection]);

  const handleUnsavedCancel = React.useCallback(() => {
    setShowUnsavedModal(false);
    pendingIngredientRef.current = null;
  }, []);

  const handlePrintPdf = () => {
    navigate("/ingridiens-control-panel", {
      state: {
        ingredient: ingredient || null,
        returnPath: "/os",
      },
    });
  };

  const handleSign = async (pin: string) => {
    if (!ingredient?._id || !signField) return;
    try {
      await signIngredient(ingredient._id, signField, pin);
      setSignField(null);
      showToast(t("qualDoc.signedSuccessfully") || "Signed successfully", "success");
    } catch (err: any) {
      // Conflict already toasted by the hook (with „Освежи") — close the PIN modal.
      if (isStaleVersionError(err)) {
        setSignField(null);
        return;
      }
      showToast(err?.response?.data?.message || t("errors.signFailed") || "Signature failed", "error");
    }
  };

  const handleRemoveSignature = (field: "prepared" | "approved") => {
    if (field !== 'prepared' || ingredient?.approved?.signature) return;
    setRemovePreparedSignature(true);
  };

  const confirmRemovePreparedSignature = async (pin: string, reason?: string) => {
    if (!ingredient?._id || !reason) return;
    try {
      await removeIngredientSignature(ingredient._id, 'prepared', pin, reason);
      setRemovePreparedSignature(false);
      showToast(t("common.savedSuccessfully") || "Saved", "success");
    } catch (error: any) {
      if (isStaleVersionError(error)) {
        setRemovePreparedSignature(false);
        return;
      }
      showToast(error?.response?.data?.message || t("errors.saveFailed") || "Save failed", "error");
      throw error;
    }
  };

  // Picture of everything the row autosave persists — compared before/after a quiet PUT.
  const formValuesRef = React.useRef("");
  formValuesRef.current = JSON.stringify([
    razmerRows, materialnoGroups, productivityRows, subprocessItemRows, inProcessValues, montageValues,
    batch, waste, plannedWaste, validFrom, validTo, productionCode,
  ]);

  // `quiet` = row autosave (useStageAutosave): no full-screen spinner, no reload, no toasts —
  // a blocked save just stays dirty for the toolbar Save, which explains itself.
  const saveIngredient = async (options?: { quiet?: boolean }): Promise<boolean> => {
    const quiet = options?.quiet === true;
    if (isArchiveView) return false; // read-only archive view — never persist
    // Validation
    if (!selectedItemId || status === undefined || status === null) {
      if (!quiet) setShowValidationErrors(true);
      return false;
    }

    // Подпроцесот е ЗАДОЛЖИТЕЛЕН на секој пополнет ред од Размерување. Ред без фаза е сирак:
    // не се засадува во Материјално, не ја внесува својата фаза во левото дрво (види
    // selectedSubprocessIds) и постои само во таа една табела. Проверката е тука, а не само во
    // табелата, зашто затекнатите (демо / пред-миграциски) редови носат празен `subprocess` и
    // мораат да се исчистат пред следното зачувување.
    const orphanRazmerIdx = razmerRows.findIndex(razmerRowMissingSubprocess);
    if (orphanRazmerIdx >= 0) {
      if (!quiet) showToast(t('os.razmer.subprocessRequired', { row: orphanRazmerIdx + 1 }), 'error');
      return false;
    }

    if (!quiet) setShowValidationErrors(false);

    const data = {
      items: selectedItemId,
      batch,
      batchUnit: batchUnit || undefined,
      waste,
      plannedWaste,
      // Drop only fully-empty rows; keep rows that have any content (item, process, machine, text, or params).
      formulationBatch: formulationBatchRows
        .filter((r) => !!r.item || !!r.process || !!r.processDefinition || !!r.machine || (!!r.text && r.text.trim() !== '') || (r.params && Object.keys(r.params).length > 0))
        .map((r) => ({
          _id: r._id,
          text: r.text || '',
          item: r.item || undefined,
          process: r.process || undefined,
          processDefinition: r.processDefinition || undefined,
          machine: r.machine || undefined,
          machineRunTimeMin: r.machineRunTimeMin ?? null,
          cleaningTimeMin: r.cleaningTimeMin ?? null,
          machineInstructions: r.machineInstructions || '',
          procedure: r.procedure ?? '',
          operatorCount: r.operatorCount ?? null,
          waste: r.rowWaste ?? null,
          priority: r.priority ?? null,
          quantity: r.quantity ?? null,
          // null, NEVER '' — Mongoose cannot cast '' to ObjectId and the whole save would fail
          mainUnit: r.mainUnit || null,
          params: r.params ?? null,
          qualDoc: r.qualDoc || null,
        })),
      consumedInGroups: consumedInGroups
        .filter((group) => !!group.item)
        .map((group) => ({
          item: group.item,
          quantity: group.quantity,
          unitMeasurement: group.unitMeasurement || null,
          priority: group.priority ?? null,
          process: group.process || null,
          subprocess: group.subprocess || null,
          waste: group.waste ?? null,
        })),
      productionCode,
      validFrom,
      validTo,
      workOrderDocument: selectedWorkOrderDocumentId || undefined,
      status,
      preparedUserId: preparedUserId || null,
      preparedDate: preparedDate || null,
      approvedUserId: approvedUserId || null,
      approvedDate: approvedDate || null,
      equipmentId,
      subprocesses: selectedSubprocessIds,
      productivity: productivityRows
        // A row of a stage that nothing holds any more is dropped, or the server re-adds the stage.
        .filter((r) => !r.subprocessId || r.subprocessId === RAZMER_STAGE_ID || selectedSubprocessIds.includes(r.subprocessId))
        .filter((r) =>
          r.perSubprocess ||
          r.machineId ||
          r.montageTime ||
          r.machineTime ||
          r.cleaningTime ||
          r.dismountCleanTime ||
          r.operatorCount ||
          (r.setupParams || []).some((sp) => sp.description.trim() || sp.par)
        )
        .map((r) => ({
          machine: r.machineId || null,
          montageTime: parseDecimalInput(r.montageTime),
          machineTime: parseDecimalInput(r.machineTime),
          cleaningTime: parseDecimalInput(r.cleaningTime),
          dismountCleanTime: parseDecimalInput(r.dismountCleanTime),
          operatorCount: parseDecimalInput(r.operatorCount),
          setupParams: (r.setupParams || [])
            .filter((sp) => sp.description.trim() || sp.par)
            .map((sp) => ({
              description: sp.description.trim(),
              par: parseDecimalInput(sp.par),
            })),
          // Размерување is a pinned pseudo-stage, not a Subprocess document. Keep the
          // frontend sentinel out of the ObjectId ref and persist membership via isRazmer.
          subprocess: r.subprocessId === RAZMER_STAGE_ID ? null : (r.subprocessId || null),
          isRazmer: r.subprocessId === RAZMER_STAGE_ID,
          // Sent only when true — an old per-machine row must round-trip unchanged so the
          // approved-normativ lock (server gmpFieldChanged) sees no difference.
          ...(r.perSubprocess ? { perSubprocess: true } : {}),
        })),
      razmer: razmerRows
        // Same predicate the table uses to decide "is this row real?" — shared so the save
        // filter and the mandatory-Подпроцес check can never drift apart.
        .filter(razmerRowHasData)
        .map((r) => ({
          phaseType: r.phaseType || null,
          priority: parseDecimalInput(r.priority),
          item: r.itemId || null,
          requiredQty: parseDecimalInput(r.requiredQty),
          toleranceLower: parseDecimalInput(r.toleranceLower),
          toleranceUpper: parseDecimalInput(r.toleranceUpper),
          allowedWaste: parseDecimalInput(r.allowedWaste),
          // Изборот на парсер живее во OSRazmerTable — рачниот override е кориснички внес,
          // пресметаната вредност е сопствен en-US стринг. Види `allowedRangeNumberOf`.
          allowedYield: allowedRangeNumberOf(r),
          allowedRangeManual: r.allowedRangeManual || "",
          sieve: r.sieve || "",
          // Picked per row in the Размерување table's first column. Размерување stays a
          // process-level STAGE (one table, all rows — never sliced by activeStage), but each
          // row tags which subprocess it belongs to. Must be a real Subprocess _id or null —
          // `razmer.subprocess` is a Mongoose ref('Subprocess') and gets `.populate()`d, so
          // the RAZMER_STAGE_ID sentinel must never reach it.
          subprocess: r.subprocessId || null,
        })),
      interProducts: subprocessItemRows
        .filter((r) => r.subprocessId || r.issueSubprocessId || r.itemId || r.quantity || r.secondaryQty || r.allowedWaste || r.allowedYield)
        .map((r) => ({
          subprocess: r.subprocessId || null,
          issueSubprocess: r.issueSubprocessId || null,
          item: r.itemId || null,
          quantity: parseDecimalInput(r.quantity),
          secondaryQty: parseDecimalInput(r.secondaryQty),
          allowedWaste: parseDecimalInput(r.allowedWaste),
          // Исто како кај razmer: ова е сопствен форматиран излез, не внес од корисник.
          allowedYield: parseDecimalInput(allowedYieldOf(r)),
        })),
      materialno: regroupedMaterialnoGroups
        .map((g) => ({
          procedure: g.procedure,
          instruction: g.instruction,
          priority: parseDecimalInput(g.priority),
          manual: !!g.manual,
          items: g.items
            .filter((it) => it.itemId || it.quantity)
            .map((it) => ({
              item: it.itemId || null,
              quantity: parseDecimalInput(it.quantity),
            })),
          subprocess: g.subprocessId || null,
        }))
        .filter((g) => g.manual || g.procedure || g.instruction || g.items.length > 0),
      // Only entries belonging to a still-selected subprocess are persisted: deselecting a
      // stage must not leave its values behind in the document.
      inProcessParams: Object.entries(inProcessValues)
        .filter(([entryId, value]) => value.trim() !== "" && selectedSubprocessIds.includes(entryOwner[entryId]))
        .map(([entryId, value]) => ({ subprocess: entryOwner[entryId], entry: entryId, value })),
      montageParamValues: Object.entries(montageValues)
        .filter(([entryId, value]) => value.trim() !== "" && selectedSubprocessIds.includes(entryOwner[entryId]))
        .map(([entryId, value]) => ({ subprocess: entryOwner[entryId], entry: entryId, value })),
    };

    if (quiet) {
      // Only an existing, unapproved normativ; the PUT response carries the new version, and
      // the form is not re-synced (same _id), so the operator keeps typing undisturbed.
      if (!ingredient?._id || isCreating || ingredient.approved?.signature) return false;
      const sentFormValues = formValuesRef.current;
      try {
        await updateIngredient(ingredient._id, data, { quiet: true });
        // Something typed while the PUT ran stays dirty for the next row save.
        if (formValuesRef.current === sentFormValues) {
          setIsDirty(false);
          setHasGmpChanges(false);
        }
        return true;
      } catch {
        return false;
      }
    }

    try {
      setIsSaving(true);
      if (ingredient?._id && !isCreating) {
        const updateData = ingredient.approved?.signature
          ? { productionCode, validTo }
          : data;
        await updateIngredient(ingredient._id, updateData);
        // Reload the saved record so version display + form fields reflect the new version
        await loadIngredientById(ingredient._id);
      } else {
        await createIngredient(data);
        setIsCreating(false);
      }
      setIsSaving(false);
      setIsDirty(false);
      setHasGmpChanges(false);
      return true;
    } catch (error) {
      setIsSaving(false);
      return false;
    }
  };

  // Размерување (weighing) is a fixed, pinned, process-level stage — not driven by any
  // Subprocess/processDefinition. It shows the weighing table; all other (subprocess)
  // stages show Материјално.
  const isApproved = Boolean(ingredient?.approved?.signature);
  const isEditable = !isArchiveView && !isApproved;

  const handleSaveIngredient = () => {
    if (isArchiveView) return;
    if (ingredient?.prepared?.signature && hasGmpChanges) {
      setConfirmPreparedSave(true);
      return;
    }
    void saveIngredient();
  };
  // Row autosave on the stage tables (owner decision 2026-10-09): one save per finished row,
  // each one a GMP history entry. Never on a new / approved normativ, and never when it would
  // silently invalidate the Подготвил signature — that still goes through the confirm modal.
  const stageAutosave = useStageAutosave({
    trigger: "row",
    enabled: isEditable && !!ingredient?._id && !isCreating,
    isDirty: isDirty && !(ingredient?.prepared?.signature && hasGmpChanges),
    save: () => saveIngredient({ quiet: true }),
  });
  const isRazmerStage = activeStage === RAZMER_STAGE_ID;
  const activeSubprocess = subprocesses.find((sp) => sp._id === activeStage);
  const machineById = React.useMemo(() => {
    const map = new Map<string, (typeof machineOptions)[number]>();
    machineOptions.forEach((machine) => map.set(machine._id, machine));
    return map;
  }, [machineOptions]);

  const activeSubprocessMachineIds = React.useMemo(() => {
    if (!activeSubprocess?.machines) return [];
    const seen = new Set<string>();
    return [...activeSubprocess.machines]
      .sort((a, b) => Number(a.order || 0) - Number(b.order || 0))
      .map((row) => (row.machine && typeof row.machine === 'object') ? row.machine._id : row.machine)
      .filter((id): id is string => {
        if (!id || seen.has(id)) return false;
        seen.add(id);
        return true;
      });
  }, [activeSubprocess]);

  const razmerProcessDefinition = React.useMemo(() => {
    const label = t("os.razmerStage", "Размерување").trim().toLocaleLowerCase("mk");
    return processDefinitions.find((processDefinition) => {
      const name = (processDefinition.name || "").trim().toLocaleLowerCase("mk");
      const code = (processDefinition.code || "").trim().toLocaleLowerCase("mk");
      return name === label || code === label || name.includes("размер") || name.includes("weigh");
    });
  }, [processDefinitions, t]);

  const razmerProcessMachineIds = React.useMemo(() => {
    if (!razmerProcessDefinition?.machines) return [];
    const seen = new Set<string>();
    return [...razmerProcessDefinition.machines]
      .sort((a, b) => Number(a.order || 0) - Number(b.order || 0))
      .map((row) => (row.machine && typeof row.machine === 'object') ? row.machine._id : row.machine)
      .filter((id): id is string => {
        if (!id || seen.has(id)) return false;
        seen.add(id);
        return true;
      });
  }, [razmerProcessDefinition]);

  const activeStageMachineIds = isRazmerStage ? razmerProcessMachineIds : activeSubprocessMachineIds;

  const defaultProductivityRow = React.useCallback((machineId: string, subprocessId: string): ProdRow => {
    const machine = machineById.get(machineId);
    const asStr = (value: number | null | undefined) => value === null || value === undefined ? "" : String(value);
    return {
      ...emptyProdRow(),
      machineId,
      montageTime: asStr(machine?.setupTime ?? null),
      dismountCleanTime: asStr(machine?.cleaningDismountTime ?? null),
      operatorCount: asStr(subprocesses.find((sp) => sp._id === subprocessId)?.operatorCount ?? null),
      subprocessId,
    };
  }, [machineById, subprocesses]);

  const productivityRowsWithConfiguredMachines = React.useMemo(() => {
    const rows = [...productivityRows];
    // A Log Book-per-subprocess stage already carries its machines' times (logbookStage.ts).
    const seedable = (stageId: string) => !isPerSubprocessStage(productivityRows, stageId);
    const existing = new Set(
      rows
        .filter((row) => row.subprocessId && row.machineId)
        .map((row) => `${row.subprocessId}||${row.machineId}`)
    );

    selectedSubprocessIds.forEach((subprocessId) => {
      const subprocess = subprocesses.find((sp) => sp._id === subprocessId);
      if (!subprocess?.machines || !seedable(subprocessId)) return;
      [...subprocess.machines]
        .sort((a, b) => Number(a.order || 0) - Number(b.order || 0))
        .map((row) => (row.machine && typeof row.machine === 'object') ? row.machine._id : row.machine)
        .filter(Boolean)
        .forEach((machineId) => {
          const key = `${subprocessId}||${machineId}`;
          if (existing.has(key)) return;
          existing.add(key);
          rows.push(defaultProductivityRow(machineId, subprocessId));
        });
    });

    if (razmerProcessDefinition?.machines?.length && seedable(RAZMER_STAGE_ID)) {
      const subprocessId = RAZMER_STAGE_ID;
      [...razmerProcessDefinition.machines]
        .sort((a, b) => Number(a.order || 0) - Number(b.order || 0))
        .map((row) => (row.machine && typeof row.machine === 'object') ? row.machine._id : row.machine)
        .filter((id): id is string => !!id)
        .forEach((machineId) => {
          const key = `${subprocessId}||${machineId}`;
          if (existing.has(key)) return;
          existing.add(key);
          rows.push(defaultProductivityRow(machineId, subprocessId));
        });
    }

    return rows;
  }, [productivityRows, selectedSubprocessIds, subprocesses, razmerProcessDefinition, defaultProductivityRow]);

  // Продуктивност / Размерување / Материјално are per-subprocess: the flat state arrays
  // hold rows for ALL stages (that's what gets saved); each table only ever sees the
  // slice belonging to activeStage. Legacy rows with no subprocess (null/'') never
  // appear in any slice — they are demo data, not migrated.
  const visibleProdRows = React.useMemo(() => {
    if (!activeStage) return [];
    const rows = productivityRows.filter((r) => r.subprocessId === activeStage);
    if (isPerSubprocessStage(rows, activeStage)) return rows;
    if (activeStageMachineIds.length === 0) {
      return rows.length > 0 ? rows : [{ ...emptyProdRow(), subprocessId: activeStage }];
    }
    const existingMachineIds = new Set(rows.map((r) => r.machineId).filter(Boolean));
    const seededRows = activeStageMachineIds
      .filter((machineId) => !existingMachineIds.has(machineId))
      .map((machineId) => defaultProductivityRow(machineId, activeStage));
    return rows.length > 0 || seededRows.length > 0
      ? [...rows, ...seededRows]
      : [{ ...emptyProdRow(), subprocessId: activeStage }];
  }, [productivityRows, activeStage, activeStageMachineIds, defaultProductivityRow]);

  // Log Book мора да го покаже СЕКОЈ избран подпроцес — и оној што нема ниту една машина.
  // `productivityRowsWithConfiguredMachines` засадува редови САМО по машина, а филтерот
  // подолу фрлаше сè без машина/време, па подпроцес без машина воопшто не стигнуваше до
  // групирањето по подпроцес и исчезнуваше од Вкупно. „Сите подпроцеси"
  // (`buildSubprocessSummaryRows`) отсекогаш го прикажува — двете табели си противречеа.
  //
  // Секој ред, местодржач или не, се подредува по споделениот редослед на подпроцеси
  // (buildSubprocessOrderIndex) — истиот што го користи „Сите подпроцеси" — а не по редот
  // на снимање/засадување. Размерување намерно не добива местодржач — тоа е фаза, не подпроцес.
  const totalProductivityRows = React.useMemo(() => {
    const hasContent = (r: ProdRow) => !!(
      r.machineId || r.montageTime || r.machineTime || r.cleaningTime || r.dismountCleanTime || r.operatorCount ||
      r.montageStart || r.montageEnd || r.machineStart || r.machineEnd ||
      r.dismountStart || r.dismountEnd || r.cleaningStart || r.cleaningEnd ||
      (r.setupParams || []).some((sp) => sp.description.trim() || sp.par)
    );
    const kept = productivityRowsWithConfiguredMachines.filter(hasContent);
    const covered = new Set(kept.map((r) => r.subprocessId));
    const placeholders = selectedSubprocessIds
      .filter((id) => !covered.has(id))
      .map((id) => ({ ...emptyProdRow(), subprocessId: id }));
    const orderIndex = buildSubprocessOrderIndex(subprocesses, RAZMER_STAGE_ID);
    return [...kept, ...placeholders]
      .map((row, index) => ({ row, index }))
      .sort((a, b) => {
        const ao = orderIndex.get(a.row.subprocessId) ?? Number.MAX_SAFE_INTEGER;
        const bo = orderIndex.get(b.row.subprocessId) ?? Number.MAX_SAFE_INTEGER;
        if (ao !== bo) return ao - bo;
        return a.index - b.index;
      })
      .map(({ row }) => row);
  }, [productivityRowsWithConfiguredMachines, selectedSubprocessIds, subprocesses]);

  const visibleRazmerRows = React.useMemo(() => {
    if (!isRazmerStage) return [];
    return razmerRows.length > 0 ? razmerRows : [emptyRazmerRow()];
  }, [razmerRows, isRazmerStage]);

  // Mirrors materialnoGroups so the seeding effect below can read the CURRENT groups without
  // listing them as a dependency. Listing them would re-run the effect on every Материјално
  // keystroke and instantly re-seed a row the user had just deleted; the documented behaviour
  // is that a deleted seeded row only comes back on the NEXT visit to the stage.
  const materialnoGroupsRef = React.useRef(materialnoGroups);
  materialnoGroupsRef.current = materialnoGroups;

  // „Подпроцес издавање" по ред од Меѓу/Полупроизвод. Во ОС оваа вредност НЕ се чува на редот
  // додека се уредува — таа е ИЗВЕДЕНА од синџирот (серверот ја снима дури при зачувување, види
  // normalizeInterProducts). Затоа тука се вика истата извезена функција што ја користи и самата
  // табела, со истиот `chainSubprocessIds`: инаку засаденото Материјално би можело да заврши во
  // друга фаза од онаа што ја пишува ќелијата „Подпроцес издавање" два реда погоре.
  const interProductIssueSubprocessIds = React.useMemo(
    () => deriveIssueSubprocessIds(subprocessItemRows, subprocesses, selectedSubprocessIds),
    [subprocessItemRows, subprocesses, selectedSubprocessIds]
  );

  // Whatever is weighed for the active subprocess is a material of that stage, so opening the
  // stage SEEDS those idents into Материјално as real rows — no re-typing. From then on they
  // are ordinary rows: movable between Постапки by Реден бр., editable, deletable.
  //
  // Меѓу/Полупроизводот се засадува по ИСТОТО правило, само неговата фаза се чита од друго поле:
  // материјал е на фазата што го ТРОШИ, т.е. неговиот „Подпроцес издавање" — не на „Подпроцес
  // прием", каде тој се произведува. (Блистирање издава на Картонирање → полупроизводот е
  // материјал на Картонирање.) Оттаму двата извора се спојуваат во една листа пред засадувањето:
  // де-дупликацијата, вметнувањето во првата Постапка и dirty-обележувањето мора да останат едно
  // место, инаку ист идент од двата извора би влегол двапати.
  //
  // Only idents MISSING from the stage are added, so re-opening never duplicates and a saved
  // normativ is left alone. Known trade-off of "real rows" over live mirrors: deleting a
  // seeded row re-seeds it on the next visit, because the ident is missing again — the way to
  // remove it for good is to remove its row in Размерување.
  //
  // Seeding writes REAL rows into state, so it must also mark the form dirty. Without that the
  // master header's Save (gated on isDirty) stayed greyed out while the table below already
  // showed unsaved rows, and leaving the screen dropped them with no unsaved-changes prompt.
  // Written as a plain read/compute/set instead of a functional updater precisely so "did we
  // actually seed anything?" is known here, where setIsDirty can be called.
  useEffect(() => {
    if (isArchiveView) return; // read-only archive view — never mutate the loaded record
    if (!activeStage || isRazmerStage) return;
    const weighed: MatItem[] = razmerRows
      .filter((r) => r.subprocessId === activeStage && r.itemId)
      .map((r) => ({
        itemId: r.itemId,
        ident: r.ident,
        name: r.name,
        unitCode: r.unitCode,
        quantity: r.requiredQty,
        priority: r.priority,
      }));
    const handedOver: MatItem[] = subprocessItemRows
      .filter((r, i) => r.itemId && interProductIssueSubprocessIds[i] === activeStage)
      .map((r) => ({
        itemId: r.itemId,
        ident: r.ident,
        name: r.name,
        unitCode: r.unitCode,
        quantity: r.quantity,
        // Меѓу/Полупроизводот нема Реден бр. свој — за разлика од размер редот. Останува празен
        // и корисникот го пишува во Материјално (visibleMaterialnoGroups не го прегазува: тој
        // мапира приоритет само за иденти што ИМААТ размер ред во оваа фаза).
        priority: "",
      }));
    const sources = [...weighed, ...handedOver];
    if (sources.length === 0) return;

    const prev = materialnoGroupsRef.current;
    const present = new Set<string>();
    prev.forEach((g) => {
      if (g.subprocessId !== activeStage) return;
      g.items.forEach((it) => { if (it.itemId) present.add(it.itemId); });
    });
    // `present` се доподполнува во тек на филтрирањето, па ист идент што доаѓа и од Размерување
    // и од Меѓу/Полупроизвод се засадува само еднаш.
    const missing = sources.filter((s) => {
      if (present.has(s.itemId)) return false;
      present.add(s.itemId);
      return true;
    });
    if (missing.length === 0) return;

    const seeded: MatItem[] = missing;
    const firstIdx = prev.findIndex((g) => g.subprocessId === activeStage);
    const next = firstIdx < 0
      // Priority left blank here — regroupedMaterialnoGroups (below) re-derives it from
      // Фаза on every render regardless of what this placeholder carries.
      ? [...prev, { ...emptyGroup(0, ""), subprocessId: activeStage, items: seeded }]
      : prev.map((g, i) => {
          if (i !== firstIdx) return g;
          // Drop the untouched placeholder row so the seeded ones don't sit under a blank line.
          const kept = g.items.filter((it) => it.itemId || it.quantity);
          return { ...g, items: [...kept, ...seeded] };
        });

    setMaterialnoGroups(next);
    markGmpDirty();
  }, [isArchiveView, activeStage, isRazmerStage, razmerRows, subprocessItemRows, interProductIssueSubprocessIds]);

  // Инструкции from the subprocess codebook (/subprocesses → Инструкции; entered only on packaging
  // subprocesses) are pulled into Материјално next to the input materials: each instruction fills the
  // empty Инструкција cell of this stage's material groups, in order. Only when there are more
  // instructions than material groups does the rest become instruction-only rows (manual groups)
  // after them. Texts the stage already carries are skipped, so a saved normativ is left alone.
  useEffect(() => {
    if (isArchiveView || isApproved) return;
    if (!activeStage || isRazmerStage) return;
    const instructions = (activeSubprocess?.instructions || [])
      .map((entry) => (entry.text || "").trim())
      .filter(Boolean);
    if (instructions.length === 0) return;

    const prev = materialnoGroups;
    const stageGroups = prev.filter((g) => g.subprocessId === activeStage);
    const present = new Set(stageGroups.map((g) => (g.instruction || "").trim()));
    const missing = instructions.filter((text, i) => !present.has(text) && instructions.indexOf(text) === i);
    if (missing.length === 0) return;

    // Materials are still being seeded from Размерување — wait for their rows instead of
    // creating a separate instruction row above them.
    const hasMaterialGroup = stageGroups.some((g) => !g.manual && g.items.length > 0);
    const stageHasWeighing = razmerRows.some((r) => r.subprocessId === activeStage && r.itemId);
    if (!hasMaterialGroup && stageHasWeighing) return;

    const queue = [...missing];
    const next = prev.map((g) => {
      if (queue.length === 0 || g.subprocessId !== activeStage || g.manual || g.items.length === 0) return g;
      if ((g.instruction || "").trim()) return g;
      return { ...g, instruction: queue.shift() as string };
    });

    const priorities = [
      ...stageGroups.map((g) => Number(g.priority)),
      ...razmerRows.filter((r) => r.subprocessId === activeStage).map((r) => Number(r.priority)),
    ].filter((n) => Number.isFinite(n));
    let nextPriority = Math.floor(priorities.length ? Math.max(...priorities) : 0) + 1;
    const extraRows: MatGroup[] = queue.map((text) => ({
      ...emptyGroup(0, String(nextPriority++), true),
      subprocessId: activeStage,
      instruction: text,
    }));

    setMaterialnoGroups([...next, ...extraRows]);
    markGmpDirty();
  }, [isArchiveView, isApproved, activeStage, isRazmerStage, activeSubprocess, razmerRows, materialnoGroups]);

  // The other half of the same contract: Материјално's rows are OWNED by their source table —
  // Размерување, or Меѓу/Полупроизвод for a row seeded from there.
  // OSMaterialnoTable renders Шифра/Назив/ЕМ/Количина read-only and refuses per-row deletion
  // precisely because "a row is removed by removing its source row" — but the seeding effect
  // above only ever ADDS. Weigh an ident into a stage, change your mind, delete it from
  // Размерување, and its Материјално row used to stay for good, unreachable by any control.
  //
  // Removal is computed as a DIFF against the previous render's seed keys, NOT as "drop
  // every Материјално row with no matching source row". The difference is what makes it
  // safe: a diff only deletes what the user just deleted in this session, so a legacy or
  // Excel-imported normativ carrying Материјално rows that never had a weighing row is left
  // alone instead of being silently wiped the moment it loads.
  //
  // Unlike seeding this is NOT scoped to the active stage — the row must go whether or not the
  // user ever opens that stage again, otherwise Save would still persist it.
  useEffect(() => {
    if (isArchiveView) return; // read-only archive view — never mutate the loaded record
    const keyOf = (subprocessId: string, itemId: string) => `${subprocessId}|${itemId}`;
    const current = new Set<string>();
    razmerRows.forEach((r) => { if (r.itemId) current.add(keyOf(r.subprocessId, r.itemId)); });
    // Истиот клуч што го употреби засадувањето: фазата на меѓупроизводот е неговото ИЗДАВАЊЕ.
    subprocessItemRows.forEach((r, i) => {
      const issueSubprocessId = interProductIssueSubprocessIds[i];
      if (r.itemId && issueSubprocessId) current.add(keyOf(issueSubprocessId, r.itemId));
    });

    const prev = prevSeedKeysRef.current;
    prevSeedKeysRef.current = current;
    // First run for this record (mount / load / reset) — no baseline to diff against yet.
    if (!prev) return;

    const removed = new Set<string>();
    prev.forEach((k) => { if (!current.has(k)) removed.add(k); });
    if (removed.size === 0) return;

    setMaterialnoGroups((groups) => {
      let changed = false;
      const next = groups.map((g) => {
        const kept = g.items.filter((it) => !it.itemId || !removed.has(keyOf(g.subprocessId, it.itemId)));
        if (kept.length === g.items.length) return g;
        changed = true;
        return { ...g, items: kept };
      });
      // A Постапка emptied this way is kept: its Постапка/Инструкција text is the user's own
      // and the table renders a spanning "нема размерени материјали" note for it.
      return changed ? next : groups;
    });
    markGmpDirty();
  }, [isArchiveView, razmerRows, subprocessItemRows, interProductIssueSubprocessIds]);

  // Материјално's `quantity` is not typed here — it is OWNED by Размерување (weighed rows) or
  // Меѓу/Полупроизвод (handed-over inter-product rows), the same two sources the seeding
  // effect above copies from ONCE. Without re-deriving it at render time, editing a
  // requiredQty/quantity in the source table AFTER the row was already seeded left the
  // Материјално cell holding the STALE value the seeding effect wrote once and never touched
  // again — and that stale value then flowed straight into the save payload, the Excel export
  // and the print form. This mirrors how `priority` is already re-derived below in
  // `visibleMaterialnoGroups`, just done here so the save payload and the exports see it too,
  // not only the on-screen panel.
  //
  // The stored `it.quantity` is kept ONLY as a fallback when no source row exists for that
  // key — legacy / Excel-imported Материјално rows legitimately have no weighing row at all
  // and must not be blanked just because one is missing.
  const syncedMaterialnoGroups = React.useMemo(() => {
    const bySource = new Map<string, string>();
    // Меѓу/Полупроизвод first so Размерување (added after) overwrites on a shared key — the
    // same precedence the seeding effect above uses (weighed listed before handedOver).
    subprocessItemRows.forEach((r, i) => {
      const issueSubprocessId = interProductIssueSubprocessIds[i];
      if (r.itemId && issueSubprocessId) {
        bySource.set(`${issueSubprocessId}|${r.itemId}`, r.quantity);
      }
    });
    razmerRows.forEach((r) => {
      if (r.itemId) bySource.set(`${r.subprocessId}|${r.itemId}`, r.requiredQty);
    });

    let groupsChanged = false;
    const next = materialnoGroups.map((g) => {
      let itemsChanged = false;
      const items = g.items.map((it) => {
        if (!it.itemId) return it;
        const sourceQty = bySource.get(`${g.subprocessId}|${it.itemId}`);
        if (sourceQty === undefined || sourceQty === it.quantity) return it;
        itemsChanged = true;
        return { ...it, quantity: sourceQty };
      });
      if (!itemsChanged) return g;
      groupsChanged = true;
      return { ...g, items };
    });
    return groupsChanged ? next : materialnoGroups;
  }, [materialnoGroups, razmerRows, subprocessItemRows, interProductIssueSubprocessIds]);

  // A Постапка's Реден бр. is no longer typed by hand — it is DERIVED from Фаза
  // (razmerRows[].priority, the same field `visibleMaterialnoGroups` already overlays onto
  // each item below). This mirrors the server's `regroupMaterialnoByPhase` in
  // server/utils/razmerNormativ.js, so client and server must reach the same grouping.
  // For each subprocess (own relative order preserved), items are bucketed by their razmer
  // Фаза into one group per distinct phase (ascending), plus one trailing group (priority
  // "") for items with no determinable phase (Меѓу/Полупроизвод rows, or a blank Фаза) — see
  // razmerNormativ.js's algorithm for the exact text-assignment tie-breaking rules. Pure
  // derivation, no state write — feeds the save payload, the exports, and the visible panel.
  //
  // Manual groups (g.manual === true, inserted via Shift+↑ on the Реден бр. cell — see
  // OSMaterialnoTable.tsx) are user-authored, instruction-only Постапки that are NEVER
  // re-derived from Фаза: they carry their own fractional `priority` (e.g. "1.5") which the
  // regroup logic treats as an opaque sort key, not something to recompute. Old groups are
  // first partitioned into `phaseOldGroups` (fed through the existing numbered/unphased
  // bucketing walk unchanged) and `manualOldGroups` (emitted as-is, always with items: [] —
  // any item a manual group is still defensively holding is salvaged into the normal phase
  // buckets, never left on the manual group itself, and never contributes its text to
  // `textForPhase`/`trailingTextCandidates`). Numbered and manual groups are then combined
  // and sorted ascending by priority so a manual group renders in its inserted position
  // between the numbered phases; the trailing (priority "") group always stays last.
  const regroupedMaterialnoGroups = React.useMemo(() => {
    const phaseByKey = new Map<string, number>();
    razmerRows.forEach((r) => {
      if (!r.itemId) return;
      const trimmed = (r.priority || "").trim();
      if (trimmed === "") return;
      const n = Number(trimmed);
      if (Number.isFinite(n)) phaseByKey.set(`${r.subprocessId}|${r.itemId}`, n);
    });

    // Bucket the old groups by subprocessId, preserving first-seen insertion order — that
    // order also becomes the output order across subprocesses/stages.
    const bySubprocess = new Map<string, MatGroup[]>();
    syncedMaterialnoGroups.forEach((g) => {
      const key = g.subprocessId || "";
      const bucket = bySubprocess.get(key);
      if (bucket) bucket.push(g);
      else bySubprocess.set(key, [g]);
    });

    const result: MatGroup[] = [];
    bySubprocess.forEach((oldGroups, subprocessId) => {
      const numberedBuckets = new Map<number, MatItem[]>();
      const textForPhase = new Map<number, { procedure: string; instruction: string }>();
      const unphasedBucket: MatItem[] = [];
      const trailingTextCandidates: { procedure: string; instruction: string }[] = [];

      const manualOldGroups: MatGroup[] = [];
      const phaseOldGroups: MatGroup[] = [];
      oldGroups.forEach((g) => {
        if (g.manual) manualOldGroups.push(g);
        else phaseOldGroups.push(g);
      });

      // Defensive item salvage: a manual group should never hold items, but if one somehow
      // does, route them into the same phase/unphased buckets used below — without letting
      // that group's own text join textForPhase/trailingTextCandidates.
      manualOldGroups.forEach((g) => {
        g.items.forEach((it) => {
          const phase = phaseByKey.get(`${subprocessId}|${it.itemId}`);
          if (phase !== undefined) {
            const bucket = numberedBuckets.get(phase);
            if (bucket) bucket.push({ ...it });
            else numberedBuckets.set(phase, [{ ...it }]);
          } else {
            unphasedBucket.push({ ...it });
          }
        });
      });

      phaseOldGroups.forEach((g) => {
        const phasesInG: number[] = [];
        g.items.forEach((it) => {
          const phase = phaseByKey.get(`${subprocessId}|${it.itemId}`);
          if (phase !== undefined) {
            phasesInG.push(phase);
            const bucket = numberedBuckets.get(phase);
            if (bucket) bucket.push({ ...it });
            else numberedBuckets.set(phase, [{ ...it }]);
          } else {
            unphasedBucket.push({ ...it });
          }
        });
        if (phasesInG.length > 0) {
          const lowest = Math.min(...phasesInG);
          if (!textForPhase.has(lowest)) {
            textForPhase.set(lowest, { procedure: g.procedure, instruction: g.instruction });
          }
        } else if (trailingTextCandidates.length === 0) {
          trailingTextCandidates.push({ procedure: g.procedure, instruction: g.instruction });
        }
      });

      const manualEmitted: MatGroup[] = manualOldGroups.map((g) => ({
        subprocessId,
        priority: g.priority,
        procedure: g.procedure,
        instruction: g.instruction,
        items: [],
        manual: true,
      }));

      const numberedEmitted: MatGroup[] = Array.from(numberedBuckets.keys())
        .sort((a, b) => a - b)
        .map((phase) => {
          const text = textForPhase.get(phase);
          return {
            subprocessId,
            priority: String(phase),
            procedure: text?.procedure ?? "",
            instruction: text?.instruction ?? "",
            items: numberedBuckets.get(phase) as MatItem[],
            manual: false,
          };
        });

      const combined = [...numberedEmitted, ...manualEmitted].sort(
        (a, b) => Number(a.priority) - Number(b.priority)
      );
      result.push(...combined);

      // A blank placeholder Постапка (no items, no text — the one OSMaterialnoTable puts back
      // when its last group is deleted) is not saved: it would keep the subprocess in the tree.
      const text = trailingTextCandidates[0];
      const hasText = !!text && (text.procedure.trim() !== "" || text.instruction.trim() !== "");
      if (unphasedBucket.length > 0 || hasText) {
        result.push({
          subprocessId,
          priority: "",
          procedure: text?.procedure ?? "",
          instruction: text?.instruction ?? "",
          items: unphasedBucket,
          manual: false,
        });
      }
    });

    return result;
  }, [syncedMaterialnoGroups, razmerRows]);

  const subprocessNameById = React.useMemo(
    () => new Map(subprocesses.map((subprocess) => [subprocess._id, subprocess.name || ""])),
    [subprocesses]
  );

  const visibleMaterialnoGroups = React.useMemo(() => {
    if (!activeStage) return [];
    const groups = regroupedMaterialnoGroups.filter((g) => g.subprocessId === activeStage);
    const priorityByItem = new Map(
      razmerRows
        .filter((r) => r.subprocessId === activeStage && r.itemId)
        .map((r) => [r.itemId, r.priority])
    );
    const withRazmerPriority = groups.map((g) => ({
      ...g,
      items: g.items.map((it) => ({ ...it, priority: priorityByItem.get(it.itemId) || it.priority || '' })),
    }));
    return withRazmerPriority.length > 0 ? withRazmerPriority : [{ ...emptyGroup(0, "1"), subprocessId: activeStage }];
  }, [regroupedMaterialnoGroups, activeStage, razmerRows]);

  // Stage-panel writers. The panel hands back the ACTIVE STAGE'S rows only; the flat state
  // arrays hold every stage's rows, so each write replaces just this stage's slice and stamps
  // the subprocess back onto it. Kept here rather than in the panel: the panel renders, this
  // screen owns the form state.
  const handleProductivityChange = React.useCallback((rows: ProdRow[]) => {
    if (!activeStage) return;
    setProductivityRows((prev) => [
      ...prev.filter((r) => r.subprocessId !== activeStage),
      ...rows.map((r) => ({ ...r, subprocessId: activeStage })),
    ]);
    markGmpDirty();
  }, [activeStage]);

  // Размерување is process-level: one table, all rows, never sliced by stage.
  const handleRazmerChange = React.useCallback((rows: RazmerRow[]) => {
    setRazmerRows(rows);
    markGmpDirty();
  }, []);

  const handleMaterialnoChange = React.useCallback((groups: MatGroup[]) => {
    if (!activeStage) return;
    setMaterialnoGroups((prev) => [
      ...prev.filter((grp) => grp.subprocessId !== activeStage),
      ...groups.map((grp) => ({ ...grp, subprocessId: activeStage })),
    ]);
    markGmpDirty();
  }, [activeStage]);

  const handleInProcessChange = React.useCallback((entryId: string, value: string) => {
    setInProcessValues((prev) => ({ ...prev, [entryId]: value }));
    markGmpDirty();
  }, []);

  const handleMontageChange = React.useCallback((entryId: string, value: string) => {
    setMontageValues((prev) => ({ ...prev, [entryId]: value }));
    markGmpDirty();
  }, []);

  // Every Excel export + the two menu-visibility gates. The hook resolves the codebooks it
  // needs (phase types / machines / document types) itself — they are module-cached.
  const exports = useOSExports({
    ingredient,
    selectedItemCode,
    selectedWorkOrderDocumentId,
    validFrom,
    validTo,
    productionCode,
    versionDisplay,
    batch,
    batchUnit,
    batchUnitCode,
    waste,
    plannedWaste,
    status,
    preparedUserId,
    preparedDate,
    approvedUserId,
    approvedDate,
    usersList,
    selectedSubprocessIds,
    subprocessItemRows,
    productivityRows,
    razmerRows,
    materialnoGroups: regroupedMaterialnoGroups,
    inProcessValues,
    montageValues,
  });
  // "Архивирал" in the archive info box — an ObjectId string is resolved through the same
  // user lookup the signature dropdowns use, a populated object is formatted directly.
  const formatArchiveUser = (user: Ingredient['archivedBy'] | Ingredient['updatedBy']): string => {
    if (!user) return '-';
    if (typeof user === 'string') return exports.resolveUserName(user) || user;
    return `${user.firstName || ''} ${user.lastName || ''}`.trim() || user.email || user._id;
  };

  // Title-bar status pill — display only, derived from the archive view and the signatures.
  const normativStatus: OSNormativStatus = isArchiveView
    ? "archived"
    : isApproved
      ? "approved"
      : ingredient?.prepared?.signature
        ? "prepared"
        : (isCreating || !ingredient?._id) ? "new" : "draft";

  if (loadingIngredient || isSaving) return <LoadingSpinner />;
  if (errorList) return <ErrorComponent message={errorList} />;

  return (
    <div className="min-h-screen bg-gray-100 flex">
      <div className="flex-1 flex flex-col transition-all duration-300"
        style={{
          paddingLeft: '0',
          width: '100%',
          maxWidth: '100%',
          overflowX: 'hidden',
          backgroundColor: 'var(--color-background)'
        }}>
        <div className="w-full flex-shrink-0">
          <Navbar />
        </div>
        <div className="flex-1 flex flex-col overflow-x-hidden py-1 px-4 mt-12">
          <ShortcutNavBar />
          <OSMasterHeader
            isCreating={isCreating}
            isSaving={isSaving}
            hasChanges={isArchiveView ? false : isDirty}
            onReset={resetForm}
            onSave={handleSaveIngredient}
            saveDisabled={isArchiveView}
            saveDisabledTitle={isArchiveView ? t('errors.ingredients.lockedApproved') : undefined}
            onArchive={(!isArchiveView && ingredient?._id && !isApproved) ? handleArchive : undefined}
            onNewVersion={isApproved ? handleArchive : undefined}
            normativStatus={normativStatus}
            hasIngredient={!!(ingredient?._id)}
            search={search}
            setSearch={setSearch}
            ingredients={ingredients}
            loading={loadingList}
            page={page}
            totalPages={totalPages}
            absoluteTotal={absoluteTotal}
            setPage={setPage}
            ingredientCode={selectedItemCode || undefined}
            ingredientName={displayIngredient?.items && typeof displayIngredient.items === 'object' ? displayIngredient.items.name : undefined}
            currentIngredientId={displayIngredient?._id}
            currentIngredientCode={selectedItemCode || undefined}
            onModalOpen={reloadIngredientsList}
            onCopyIngredients={handleOpenCopyIngredients}
            onPrintPdf={handlePrintPdf}
            onIngredientSelect={(ing: Ingredient) => {
              if (isArchiveView) {
                // Read-only archive view — route selection into the archive
                // machinery instead of the live-sync path. The effect on
                // [isArchiveView, selectedArchiveId, fetchArchiveDetail] picks
                // this up and syncs the read-only form.
                setSelectedArchiveId(ing._id);
                return;
              }
              if (isDirty) {
                pendingIngredientRef.current = ing;
                setShowUnsavedModal(true);
                return;
              }
              applyIngredientSelection(ing);
            }}
          />

          {/* Editor body — identical normativ layout in both modes. On /os it is editable; on
              /os/archive OSDetailHeader `locked` makes every input/select/button read-only
              (the stage tree in its right column stays clickable), fed by syncIngredientFields(archiveDetail). */}
          <OSDetailHeader
            locked={isArchiveView || Boolean(ingredient?.approved?.signature)}
            lockPointer={isArchiveView}
            stageTreeSlot={(
              <OSStageTree
                activeStage={activeStage}
                onSelect={setActiveStage}
                filterSubprocessIds={selectedSubprocessIds}
                layout="stacked"
              />
            )}
            archiveSlot={(
              <OSArchiveInfoBox
                isArchiveView={isArchiveView}
                loadingArchive={loadingArchive}
                hasArchivedRecords={archiveIngredients.length > 0}
                loadingArchiveDetail={loadingArchiveDetail}
                archiveDetail={archiveDetail}
                formatArchiveUser={formatArchiveUser}
              />
            )}
            selectedItemId={selectedItemId}
            onItemChange={handleSelectedItemChange}
            batch={batch}
            onBatchChange={(v) => { setBatch(v); markGmpDirty(); }}
            batchUnit={batchUnit}
            batchUnitCode={batchUnitCode}
            waste={waste}
            onWasteChange={(v) => { setWaste(v); markGmpDirty(); }}
            plannedWaste={plannedWaste}
            onPlannedWasteChange={(v) => { setPlannedWaste(v); markGmpDirty(); }}
            validFrom={validFrom}
            onValidFromChange={(v) => { setValidFrom(v); markGmpDirty(); }}
            validTo={validTo}
            onValidToChange={(v) => {
              setValidTo(v);
              if (isValidToExtension(v, ingredient?.validTo || '')) markGmpDirty();
              else setIsDirty(true);
            }}
            selectedWorkOrderDocumentId={selectedWorkOrderDocumentId}
            onWorkOrderDocumentChange={(v) => { setSelectedWorkOrderDocumentId(v); markGmpDirty(); }}
            versionDisplay={versionDisplay}
            productionCode={productionCode}
            onProductionCodeChange={(v) => { setProductionCode(v); setIsDirty(true); }}
            isEditable={!isArchiveView && !ingredient?.approved?.signature}
            showValidationErrors={showValidationErrors}
            ingredient={ingredient}
            isCreating={isCreating}
            isSigned={Boolean(ingredient?.prepared?.signature || ingredient?.approved?.signature)}
            preparedUserId={preparedUserId}
            setPreparedUserId={setPreparedUserId}
            preparedDate={preparedDate}
            setPreparedDate={setPreparedDate}
            approvedUserId={approvedUserId}
            setApprovedUserId={setApprovedUserId}
            approvedDate={approvedDate}
            setApprovedDate={setApprovedDate}
            usersList={usersList}
            setHasChanges={setIsDirty}
            setSignField={setSignField}
            onRemoveSignature={handleRemoveSignature}
            subprocessItemRows={subprocessItemRows}
            onSubprocessItemRowsChange={(rows) => { setSubprocessItemRows(rows); markGmpDirty(); }}
            chainSubprocessIds={selectedSubprocessIds}
          />

          {/* Navigation (Log Book toggle, process/subprocess tabs) stays clickable in the archive
              and on approved normativs; only the data panels below are locked. */}
          <div className="mb-2 flex justify-end">
            <LogBookToggleButton
              open={showTotalProductivity}
              onToggle={() => setShowTotalProductivity((value) => !value)}
            />
          </div>

          {showTotalProductivity && (
            <>
              <OSProductivityTable
                title={t('os.totalProductivity')}
                rows={totalProductivityRows}
                onChange={() => {}}
                readOnly
                groupBySubprocess
                subprocessNameById={subprocessNameById}
              />
            </>
          )}

          {/* NOTE (2026-07-28): the old „Материјално / Процес" tabs card used to sit inside this
              panel. It was wrapped in `className="hidden"`, which is display:none — React still
              MOUNTED it, so OSMaterialTable kept fetching the legacy `materialingredients`
              collection (GET /api/materialIngredients/ingredient/:id + /last-purchase-prices) on
              every /os load and every normativ switch, and kept writing into `consumedInGroups`,
              which IS part of the save payload. The whole cluster behind that card
              (OSMaterialTable / OSBatchTable / useOSMaterialSpreadsheet / OSMaterialItemCells /
              OSMotherGroupRow / OSBatchHeader / OSMaterialFilters) was DELETED on 2026-07-29 —
              it had been unreachable ever since. /rn-work-order carried the same copy-pasted
              cluster behind its own hidden card; that one was deleted on 2026-08-26, so
              MaterialIngredientsTable is the last remaining twin.
              `formulationBatch` and `consumedInGroups` are
              still synced and saved as a lossless passthrough so no persisted data is lost —
              they simply have no editor on this screen any more. */}
          <AutosaveStatusPill status={stageAutosave.status} />
          <div className="space-y-3" onBlurCapture={stageAutosave.onBlurCapture}>
            <fieldset disabled={isArchiveView || Boolean(ingredient?.approved?.signature)} className={`min-w-0 w-full border-0 p-0 m-0${isArchiveView ? ' pointer-events-none' : ''}`}>
            <OSStagePanel
              isRazmerStage={isRazmerStage}
              productivityRows={visibleProdRows}
              onProductivityChange={handleProductivityChange}
              productivityMachineIds={activeStageMachineIds}
              groupProductivityBySubprocess
              subprocessNameById={subprocessNameById}
              editableProductivitySummary
              razmerRows={visibleRazmerRows}
              onRazmerChange={handleRazmerChange}
              materialnoGroups={visibleMaterialnoGroups}
              onMaterialnoChange={handleMaterialnoChange}
              activeSubprocess={activeSubprocess}
              machineRepairsSlot={!isRazmerStage ? <OSSubprocessMachinesTable activeSubprocess={activeSubprocess} /> : undefined}
              inProcessValues={inProcessValues}
              onInProcessChange={handleInProcessChange}
              montageValues={montageValues}
              onMontageChange={handleMontageChange}
            />
            </fieldset>
          </div>
        </div>
      </div>

      <ModalConfirmation
        isOpen={confirmDeleteModal}
        onClose={handleDeleteCancel}
        onConfirm={handleDeleteConfirm}
        description={ingredient ? (typeof ingredient.items === 'object' ? ingredient.items?.name || "" : ingredient.items || "") : ""}
      />

      <ModalConfirmation
        isOpen={confirmPreparedSave}
        onClose={() => setConfirmPreparedSave(false)}
        onConfirm={() => { setConfirmPreparedSave(false); void saveIngredient(); }}
        description={null}
        title={t('ingredients.preparedSaveTitle')}
        note={t('ingredients.preparedSaveWarning')}
        confirmIcon="confirm"
        confirmButtonColor="primary"
      />
      <OSArchiveModal
        show={showArchiveModal}
        onCancel={() => setShowArchiveModal(false)}
        onConfirm={handleArchiveConfirm}
        activeVersion={versionDisplay || (ingredient?.version ? String(ingredient.version).padStart(3, '0') : '001')}
        validFrom={validFrom}
        validTo={archiveValidTo}
        createNewVersionOnly={isApproved}
        title={isApproved ? t('ingredients.archive.newVersionTitle') : undefined}
        confirmButtonText={isApproved ? t('ingredients.archive.newVersion') : undefined}
      />

      <UnsavedChangesModal
        show={showUnsavedModal}
        onCancel={handleUnsavedCancel}
        onConfirm={handleUnsavedConfirm}
      />

      <ModalSignPin
        isOpen={!!signField}
        onClose={() => setSignField(null)}
        onConfirm={handleSign}
        fieldLabel={signField ? (t(`qualDoc.${signField}`) || signField) : ""}
      />
      <ModalSignPin
        isOpen={removePreparedSignature}
        onClose={() => setRemovePreparedSignature(false)}
        onConfirm={confirmRemovePreparedSignature}
        fieldLabel={t('qualDoc.prepared') || 'prepared'}
        reasonRequired
        title={t('ingredients.unsign.title')}
        submitLabel={t('ingredients.unsign.confirm')}
      />
    </div>
  );
};

// Wrapper component to provide ItemCacheContext
interface OSProps {
  archiveView?: boolean;
}

const OS: React.FC<OSProps> = ({ archiveView = false }) => {
  return (
    <ItemCacheProvider>
      <OSInner archiveView={archiveView} />
    </ItemCacheProvider>
  );
};

export default OS;
